import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, of, throwError, timer } from 'rxjs';
import { retry, retryWhen, delay, take, concatMap, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { tariffContextForSpeciality } from '../data/app.data';
import { readCachedCodebook, writeCachedCodebook } from './procedure-codebook-cache';
import { SocketService } from './socket.service';

export interface IcdCode {
  id?: string;  // Added ID property for server-side identification
  code: string;
  description: string;
  pmbCodes?: string

  // ── SA MIT validity flags (assets/icd10-codes-enriched.json) ──────────────────────
  //
  // ⚠ ABSENT MEANS THE PERMISSIVE DEFAULT, not "unknown". The asset omits default values to
  // stay small, so `validPrimary === undefined` means the code MAY lead. Reading absence as
  // false would flag ~35,000 codes as invalid. See core/engines/icd-rules-engine.
  /** false when the code needs a more specific child (M54.5 -> M54.55). Default true. */
  validClinicalUse?: boolean;
  /** false when the code may never be the primary diagnosis. Default true. */
  validPrimary?: boolean;
  /** true for a manifestation (asterisk) code. Default false. */
  validAsterisk?: boolean;
  /** true for an aetiology (dagger) code. Default false. */
  validDagger?: boolean;
  validSequelae?: boolean;
  isDiscontinued?: boolean;
  isMorphology?: boolean;
  /** Date the code left the SA MIT, when it has. */
  saEndDate?: string;
  /** 'M' | 'F' when the MIT restricts the code by sex. */
  gender?: string;
  ageFrom?: number;
  ageTo?: number;
  /** Named partners only — the MIT's lists are non-exhaustive. */
  daggerPartners?: string;
  asteriskPartners?: string;

  // ── Captured on the record, not from the codebook ──────────────────────────────────
  /** When the condition was documented. Set from the note by the AI path; editable manually. */
  onsetDate?: string | null;
  /** primary | secondary | comorbidity | complication | external-cause | morphology */
  codeRole?: string | null;
}


export interface ProcedureCode {
  id?: string;  // Added ID property for server-side identification
  date: string;
  code: string;
  description: string;
  /** Gone from the codebook (it was wrong and nothing used it); kept optional for stored rows. */
  units?: string;
  type: string;
  tags: string[];
  /** 'medical' | 'alternative:072' | … — a code number is only unique within its context. */
  tariffContext?: string;
  modifierType?: string;
  applyTo?: string;
  priceOverride?: number;
  startTime?: string;
  endTime?: string;
  assistantName?: string;
  assistantPracticeNumber?: string;
  weight?: string;
  height?: string;
  BMI?: string;
  paidCash?: boolean;
  noCharge?: boolean;
  appointmentTypeId?: string;
  overrideDescription?: string;
  displayIndex?: number; // Added for drag and drop ordering
  // Connect (switching platform) claim lifecycle fields
  status?: 'active' | 'removed';
  source?: 'clinical' | 'connect_added';
  connectClaimStatus?: string;
  connectSubmissionStatus?: string;
  /** MediKredit's verdict for THIS line: approved | rejected | warning | duplicate.
   *  Distinct from connectClaimStatus (the whole claim, copied onto every line).
   *  The only signal a 0.00 structural modifier has — its <FIN/> is empty by design. */
  connectLineStatus?: string;
  connectBilledCents?: number;
  connectApprovedCents?: number;
  connectRejectedCents?: number;
  connectPatientLiableCents?: number;
  connectPaidCents?: number;
  connectClawbackCents?: number;
  connectClawbackAt?: string;
}

export interface LancetTest {
  orderCode: string;
  lancetPrintNumber: string;
  mnemonic?: string | null;
  name: string;
  type: string;
  units?: string;
  defaultLoinc?: string | null;
  isNumeric: boolean;
  isMicro?: boolean;
  meta?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AppDataService {
  private icd10Codes!: any[];
  private procedureCodes!: any[];
  /** Server version of `procedureCodes`; 0 until a copy (cached or fetched) is in hand. */
  private procedureCodebookVersion = 0;
  private procedureCodebookCheckedAt = 0;
  private procedureCodebookInflight: Promise<void> | null = null;
  /** True while `procedureCodes` is the bundled fallback list rather than a server/cached copy. */
  private procedureCodebookOnFallback = false;
  private procedureCodebookFallbackCodes: any[] | null = null;
  private procedureCodebookRetryTimer: ReturnType<typeof setInterval> | null = null;
  /** How often to re-check the server while on the fallback list (no push arrives when a database recovers). */
  private procedureCodebookFallbackRetryMs = 2 * 60 * 1000;
  private nappiCodes!: any[];
  private lancetTests!: LancetTest[];
  apiUrl!: 'api';

  // Retry configuration
  private readonly maxRetries = 3;
  private readonly baseDelay = 1000; // 1 second

  /** Minimum gap between unforced version checks (resume and reconnect can fire together). */
  private readonly procedureCodebookCheckGapMs = 30 * 1000;

  constructor(private http: HttpClient, private socketService: SocketService) {
    this.loadIcd10Codes();
    this.loadProcedureCodes();
    this.loadNappiCodes();
    this.loadLancetTests();
    this.watchProcedureCodebook();
  }

  private retryWithBackoff<T>(maxRetries: number = this.maxRetries, baseDelay: number = this.baseDelay) {
    return (source: Observable<T>) => {
      return source.pipe(
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, index) => {
              if (index >= maxRetries - 1) {
                console.error(`Max retries (${maxRetries}) exceeded. Giving up.`);
                return throwError(error);
              }
              const delayTime = baseDelay * Math.pow(2, index); // Exponential backoff
              console.warn(`Retry attempt ${index + 1}/${maxRetries} after ${delayTime}ms`);
              return timer(delayTime);
            })
          )
        )
      );
    };
  }

  /**
   * The SA ICD-10 reference list.
   *
   * `icd10-codes-enriched.json` replaced `icd10-codes-pmb.json` here. Same 41,008 codes, same
   * descriptions, same PMB mapping — plus the MIT validity flags the coding rules need, and
   * 0.6 MB SMALLER, because the generator omits default values instead of writing
   * `validClinicalUse: true` thirty-five thousand times.
   *
   * ⚠ `icd10-codes-pmb.json` is still in assets and must stay there: it is the ONLY source of
   * the PMB mapping (the MIT does not carry PMB codes) and
   * `scripts/build-icd10-codebook.js` reads it. It is build input now, not a runtime asset.
   *
   * Regenerate with `node scripts/build-icd10-client-asset.js` after rebuilding the codebook.
   */
  private loadIcd10Codes() {
    const sub = this.http.get<any[]>('assets/icd10-codes-enriched.json')
      .pipe(this.retryWithBackoff())
      .subscribe(
        codes => {
          this.icd10Codes = codes;
          console.log(`ICD-10 codes loaded successfully (${codes?.length ?? 0})`);
          sub.unsubscribe();
        },
        error => {
          console.error('Failed to load ICD-10 codes after retries:', error);
          sub.unsubscribe();
        }
      );
  }

  /**
   * The procedure codebook, from `GET /api/procedure-codes` (tasks/procedure-codebook.md Phase 2).
   * It replaced the bundled `assets/all-specialties-tagged-procedure-codes.json`, so codes change
   * without an app release.
   *
   * 1. Show the IndexedDB copy straight away, if there is one (instant start, offline on native).
   * 2. Ask the server for its version, and download the list only if it differs.
   * 3. If that leaves no codes at all (server unreachable or failing, or serving its own fallback,
   *    and nothing cached), use the bundled `assets/procedure-codebook-fallback.json`. See
   *    `useFallbackProcedureCodes` for the rules.
   *
   * Step 2 needs a login. Before login it is skipped, and the socket reconnect that follows login
   * (`reconnected$`) runs it. See `watchProcedureCodebook` for the other triggers.
   */
  private async loadProcedureCodes() {
    const cached = await readCachedCodebook();
    if (cached && !this.procedureCodes?.length) {
      this.procedureCodes = cached.codes;
      this.procedureCodebookVersion = cached.version;
      console.log(`Procedure codes loaded from cache (v${cached.version}, ${cached.codes.length})`);
    }
    await this.refreshProcedureCodes(true);
  }

  /** When to re-check: after (re)connecting, when the app comes back to the foreground, and on a push. */
  private watchProcedureCodebook() {
    this.socketService.reconnected$.pipe(filter(Boolean)).subscribe(() => this.refreshProcedureCodes());
    // Payload-free, like `systemStatusChanged`: re-check the version rather than trust a payload.
    this.socketService.listen('procedureCodebookChanged', 'app-data-service')
      .subscribe(() => this.refreshProcedureCodes(true));
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.refreshProcedureCodes();
      });
    }
  }

  private refreshProcedureCodes(force = false): Promise<void> {
    if (this.procedureCodebookInflight) return this.procedureCodebookInflight;
    if (!force && Date.now() - this.procedureCodebookCheckedAt < this.procedureCodebookCheckGapMs) return Promise.resolve();
    const token = typeof localStorage === 'undefined' ? null : localStorage.getItem('token');
    if (!token) return Promise.resolve(); // pre-login (or server render); reconnected$ retries after login

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.procedureCodebookInflight = (async () => {
      try {
        const { version, fallback } = await firstValueFrom(
          this.http.get<{ version: number; fallback?: 'unavailable' | 'forced' | null }>(
            `${environment.API_URL}/api/procedure-codes/version`, { headers }));
        if (fallback === 'forced') {
          await this.useFallbackProcedureCodes('the server is switched to the fallback list', true);
          return;
        }
        if (!version) {
          // The server has no database codebook. A copy we already have is newer than any fallback.
          console.error('Procedure codebook: the server has no codes (version 0)');
          if (!this.hasServerProcedureCodes()) await this.useFallbackProcedureCodes('the server has no codebook');
          return;
        }
        if (version === this.procedureCodebookVersion && this.hasServerProcedureCodes()) return;

        const body = await firstValueFrom(
          this.http.get<{ version: number; codes: any[] }>(`${environment.API_URL}/api/procedure-codes`, { headers })
            .pipe(this.retryWithBackoff()));
        if (!body?.codes?.length || !body.version) {
          if (!this.hasServerProcedureCodes()) await this.useFallbackProcedureCodes('the server returned no codes');
          return;
        }
        this.procedureCodes = body.codes;
        this.procedureCodebookVersion = body.version;
        this.leaveProcedureCodebookFallback();
        console.log(`Procedure codes loaded (v${body.version}, ${body.codes.length})`);
        await writeCachedCodebook(body.version, body.codes);
      } catch (error) {
        // Keep the cached copy. The next trigger tries again.
        console.error('Procedure codebook refresh failed:', error);
        if (!this.hasServerProcedureCodes()) await this.useFallbackProcedureCodes('the server could not be reached');
      } finally {
        this.procedureCodebookCheckedAt = Date.now();
        this.procedureCodebookInflight = null;
      }
    })();
    return this.procedureCodebookInflight;
  }

  /** A list from the server or the IndexedDB cache, i.e. anything better than the bundled fallback. */
  private hasServerProcedureCodes(): boolean {
    return !!this.procedureCodes?.length && !this.procedureCodebookOnFallback;
  }

  /**
   * The last line of defence: the bundled `assets/procedure-codebook-fallback.json`, generated from
   * the seeded database by `scripts/build-procedure-codebook-fallback.js` and shipped in every web
   * and native build, so pickers are never empty.
   *
   * - Used only when there is no server or cached copy, or when the server says it is `forced` onto
   *   its own fallback (`PROCEDURE_CODEBOOK_SOURCE=fallback`, the switch for a database copy that is
   *   reachable but wrong). `force` replaces an in-memory copy for that reason; IndexedDB is left
   *   alone, and the next real version replaces both.
   * - Never written to IndexedDB, and the version stays 0, so the first real server version always
   *   downloads over it.
   * - While on it, re-check the server every `procedureCodebookFallbackRetryMs`: a database that
   *   recovers sends no push.
   * - Never throws. If the asset can't be read either, whatever is in memory stays.
   */
  private async useFallbackProcedureCodes(reason: string, force = false): Promise<void> {
    if (!force && this.hasServerProcedureCodes()) return;
    try {
      if (!this.procedureCodebookFallbackCodes) {
        const body = await firstValueFrom(
          // Bundled (native) or same-origin (web): a quick retry, not the API's multi-second backoff.
          this.http.get<{ codes: any[] }>('assets/procedure-codebook-fallback.json').pipe(retry(2)));
        if (!body?.codes?.length) throw new Error('the fallback asset has no codes');
        this.procedureCodebookFallbackCodes = body.codes;
      }
      if (!this.procedureCodebookOnFallback) {
        console.error(`Procedure codebook: using the bundled fallback list (${this.procedureCodebookFallbackCodes.length} codes) because ${reason}`);
      }
      this.procedureCodes = this.procedureCodebookFallbackCodes;
      this.procedureCodebookVersion = 0;
      this.procedureCodebookOnFallback = true;
      if (!this.procedureCodebookRetryTimer) {
        this.procedureCodebookRetryTimer = setInterval(() => this.refreshProcedureCodes(true), this.procedureCodebookFallbackRetryMs);
      }
    } catch (error) {
      console.error('Procedure codebook: the bundled fallback list could not be loaded either:', error);
    }
  }

  private leaveProcedureCodebookFallback() {
    if (this.procedureCodebookOnFallback) console.log('Procedure codebook: back on the server list');
    this.procedureCodebookOnFallback = false;
    if (this.procedureCodebookRetryTimer) {
      clearInterval(this.procedureCodebookRetryTimer);
      this.procedureCodebookRetryTimer = null;
    }
  }

  /**
   * The codebook row for a code, in the right tariff context. The same number is a different item
   * in different contexts (physio 0001 "Appointment not kept" vs a medical modifier), so a bare
   * `find(c => c.code === code)` returns whichever came first. With a speciality, prefer its
   * context; without one, prefer medical, the context every non-allied practice bills in.
   */
  private findProcedureCode(code: string, groupSpeciality?: string | null): any {
    const matches = (this.procedureCodes || []).filter(c => c.code === code);
    if (matches.length <= 1) return matches[0];
    const context = tariffContextForSpeciality(groupSpeciality);
    return matches.find(c => c.tariffContext === context) || matches[0];
  }

  private loadNappiCodes() {
    /* this.http.get<any[]>('api/nappicodes').subscribe(
      nappiCodes => {
        this.nappiCodes = nappiCodes;
      },
      error => {
        console.log('Error loading nappi codes', error)
      }
    ) */
  }

  private loadLancetTests() {
    const sub = this.http.get<any>('assets/lancet-tests.json')
      .pipe(this.retryWithBackoff())
      .subscribe(
        data => {
          // Only use orders, not results
          this.lancetTests = data.orders;
          console.log('Lancet tests loaded successfully');
          sub.unsubscribe();
        },
        error => {
          console.error('Failed to load Lancet tests after retries:', error);
          sub.unsubscribe();
        }
      );
  }

  isCodeAModifier(code: ProcedureCode, groupSpeciality?: string | null): boolean {
    return this.findProcedureCode(code.code, groupSpeciality)?.type === 'modifier';
  }

  isCodeAConsult(code: string, groupSpeciality?: string | null): boolean {
    return this.findProcedureCode(code, groupSpeciality)?.type === 'consultation';
  }

  searchCodes(query: string, type: 'icd' | 'pc' | 'npc' | 'lancet', modifiersOnly: boolean = false, modifierTypes: ('Reduce' | 'Add' | 'Info' | 'Compound')[] = [], groupSpeciality: string | undefined = undefined): Observable<any[]> {
    return new Observable((observer: any) => {
      if (type === 'icd') {
        if (!this.icd10Codes || !this.icd10Codes.length || !query || !query.length) {
          observer.next([]); // Return an empty array if codes are not loaded or query is blank
          observer.complete();
          return;
        }
      }

      if (type === 'pc') {
        if (!this.procedureCodes || !this.procedureCodes.length || !query || !query.length) {
          observer.next([]); // Return an empty array if codes are not loaded or query is blank
          observer.complete();
          return;
        }
      }

      if (type === 'npc') {
        if (!this.nappiCodes || !this.nappiCodes.length || !query || !query.length) {
          observer.next([]); // Return an empty array if codes are not loaded or query is blank
          observer.complete();
          return;
        } else {
          const results = this.searchNappiCodesInternal(query, type, modifiersOnly);
          observer.next(results);
          observer.complete();
        }
      }

      if (type === 'lancet') {
        if (!this.lancetTests || !this.lancetTests.length || !query || !query.length) {
          observer.next([]); // Return an empty array if tests are not loaded or query is blank
          observer.complete();
          return;
        } else {
          const results = this.searchLancetTestsInternal(query);
          observer.next(results);
          observer.complete();
        }
      }

      const results = this.searchCodesInternal(query, type, modifiersOnly, modifierTypes, groupSpeciality);
      observer.next(results);
      observer.complete();
    });
  }

  private searchNappiCodesInternal(query: string, type: 'icd' | 'pc' | 'npc', modifiersOnly: boolean = false): IcdCode[] {
    const lowerQuery = query.toLowerCase();
    const queryWords = lowerQuery.split(' ');

    let codeDB = type === 'npc' ? this.nappiCodes : [];

    codeDB = modifiersOnly ? codeDB.filter(c => c.type === 'modifier') : codeDB;

    const filteredCodes = codeDB
      .filter(code =>
        queryWords.every(word =>
          code.nappiCode.toLowerCase().includes(word) ||
          code.name.toLowerCase().includes(word)
        )
      )
      .map(code => ({
        ...code,
        similarity: Math.min(
          this.levenshteinDistance(lowerQuery, code.nappiCode.toLowerCase()),
          this.levenshteinDistance(lowerQuery, code.name.toLowerCase())
        )
      }))
      .sort((a, b) => a.similarity - b.similarity);

    return filteredCodes.slice(0, 50); // Limit to top 50 results
  }

  private searchLancetTestsInternal(query: string): LancetTest[] {
    const lowerQuery = query.toLowerCase();
    const queryWords = lowerQuery.split(' ');

    const filteredTests = this.lancetTests
      .filter(test =>
        queryWords.every(word =>
          test.orderCode?.toLowerCase().includes(word) ||
          test.mnemonic?.toLowerCase().includes(word) ||
          test.name?.toLowerCase().includes(word) ||
          test.lancetPrintNumber?.toLowerCase().includes(word)
        )
      )
      .map(test => ({
        ...test,
        similarity: Math.min(
          this.levenshteinDistance(lowerQuery, test.orderCode?.toLowerCase() || ''),
          this.levenshteinDistance(lowerQuery, test.mnemonic?.toLowerCase() || ''),
          this.levenshteinDistance(lowerQuery, test.name?.toLowerCase() || '')
        )
      }))
      .sort((a, b) => a.similarity - b.similarity);

    return filteredTests.slice(0, 50); // Limit to top 50 results
  }

  private searchCodesInternal(query: string, type: 'icd' | 'pc' | 'npc' | 'lancet', modifiersOnly: boolean = false, modifierTypes: ('Reduce' | 'Add' | 'Info' | 'Compound')[] = [], groupSpeciality: string | undefined = undefined): IcdCode[] {
    const lowerQuery = query.toLowerCase();
    const queryWords = lowerQuery.split(' ');

    let codeDB = type === 'icd' ? this.icd10Codes : type === 'lancet' ? this.lancetTests : this.procedureCodes;

    if (type !== 'lancet') {
      codeDB = modifiersOnly ? codeDB.filter(c => c.type === 'modifier') : codeDB;
      codeDB = modifierTypes.length > 0 ? codeDB.filter((c) => modifierTypes.includes(c.modifierType)) : codeDB;
      if (type === 'pc') {
        // Only the group's own tariff context. This replaces the "physio sees physio-tagged codes,
        // everyone else sees the rest" rule, which was the same thing for the only two contexts
        // the asset file had.
        const context = tariffContextForSpeciality(groupSpeciality);
        codeDB = codeDB.filter((c) => c.tariffContext === context);
      }
    }

    const filteredCodes = codeDB
      .filter(code => {
        if (type === 'lancet') {
          return queryWords.every(word =>
            code.orderCode?.toLowerCase().includes(word) ||
            code.mnemonic?.toLowerCase().includes(word) ||
            code.name?.toLowerCase().includes(word) ||
            code.lancetPrintNumber?.toLowerCase().includes(word)
          );
        } else {
          return queryWords.every(word =>
            code.code.toLowerCase().includes(word) ||
            code.description.toLowerCase().includes(word)
          );
        }
      })
      .map(code => {
        if (type === 'lancet') {
          return {
            ...code,
            similarity: Math.min(
              this.levenshteinDistance(lowerQuery, code.orderCode?.toLowerCase() || ''),
              this.levenshteinDistance(lowerQuery, code.mnemonic?.toLowerCase() || ''),
              this.levenshteinDistance(lowerQuery, code.name?.toLowerCase() || '')
            )
          };
        } else {
          return {
            ...code,
            similarity: Math.min(
              this.levenshteinDistance(lowerQuery, code.code.toLowerCase()),
              this.levenshteinDistance(lowerQuery, code.description.toLowerCase())
            )
          };
        }
      })
      .sort((a, b) => a.similarity - b.similarity);

    return filteredCodes.slice(0, 50); // Limit to top 50 results
  }

  performDirectLookup(code: string, type: 'icd' | 'pc' | 'npc' | 'lancet', groupSpeciality?: string | null): any {
    if (type === 'icd') {
      return this.icd10Codes.find(c => c.code === code);
    } else if (type === 'pc') {
      return this.findProcedureCode(code, groupSpeciality);
    } else if (type === 'lancet') {
      return this.lancetTests.find(t => t.orderCode === code);
    }
  }

  getCodeDescription(code: string, groupSpeciality?: string | null): string {
    return this.findProcedureCode(code, groupSpeciality)?.description || '';
  }

  getLancetTestDescription(orderCode: string): string {
    const test = this.lancetTests.find(t => t.orderCode === orderCode);
    return test?.name || '';
  }

  // Utility methods to check if data is loaded
  isIcd10CodesLoaded(): boolean {
    return this.icd10Codes && this.icd10Codes.length > 0;
  }

  isProcedureCodesLoaded(): boolean {
    return this.procedureCodes && this.procedureCodes.length > 0;
  }

  isLancetTestsLoaded(): boolean {
    return this.lancetTests && this.lancetTests.length > 0;
  }

  isAllDataLoaded(): boolean {
    return this.isIcd10CodesLoaded() && this.isProcedureCodesLoaded() && this.isLancetTestsLoaded();
  }

  private levenshteinDistance(a: string, b: string): number {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix = [];

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  }

}